"use client";

import { useEffect, useState, useCallback } from "react";
import { useRouter } from "next/navigation";

/**
 * Client-side JSON Hot-Reload Hook
 *
 * Provides automatic page refresh when JSON files change in development
 */
export function useJSONHotReload(enabled: boolean = true) {
  const router = useRouter();
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const refreshPage = useCallback(() => {
    setLastUpdate(new Date());
    // Use router.refresh() for soft refresh that preserves scroll position
    router.refresh();
  }, [router]);

  useEffect(() => {
    // Enable with JSON mock data
    const isEnabled = process.env.USE_JSON_MOCK === "true";

    if (!isEnabled || !enabled) {
      return;
    }

    let eventSource: EventSource | null = null;
    let reconnectTimer: NodeJS.Timeout | null = null;

    const connectToSSE = () => {
      try {
        eventSource = new EventSource("/api/json-hot-reload");

        eventSource.onopen = () => {
          console.log("🔥 JSON Hot-Reload: Connected to server");
          setIsConnected(true);

          // Clear any reconnection timer
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === "csv-change" || data.type === "json-change") {
              console.log(`🔥 JSON Hot-Reload: File changed - ${data.file}`);
              refreshPage();
            }
          } catch (error) {
            console.error("JSON Hot-Reload: Error parsing SSE message", error);
          }
        };

        eventSource.onerror = (error) => {
          console.warn("🔥 JSON Hot-Reload: Connection error", error);
          setIsConnected(false);

          // Close current connection
          if (eventSource) {
            eventSource.close();
          }

          // Attempt to reconnect after 3 seconds
          if (!reconnectTimer) {
            reconnectTimer = setTimeout(() => {
              console.log("🔥 JSON Hot-Reload: Attempting to reconnect...");
              connectToSSE();
            }, 3000);
          }
        };
      } catch (error) {
        console.error("JSON Hot-Reload: Failed to connect to SSE", error);
        setIsConnected(false);
      }
    };

    // Initial connection
    connectToSSE();

    // Cleanup on unmount
    return () => {
      if (eventSource) {
        eventSource.close();
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      setIsConnected(false);
    };
  }, [enabled, refreshPage]);

  return {
    isConnected,
    lastUpdate,
    refreshPage,
  };
}

/**
 * JSON Hot-Reload Status Component
 *
 * Shows connection status in development mode
 */
interface JSONHotReloadStatusProps {
  enabled?: boolean;
  showIndicator?: boolean;
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
}

export function JSONHotReloadStatus({
  enabled = true,
  showIndicator = true,
  position = "bottom-right",
}: JSONHotReloadStatusProps) {
  const { isConnected, lastUpdate } = useJSONHotReload(enabled);

  // Enable with JSON mock data
  const isEnabled = process.env.USE_JSON_MOCK === "true";

  if (!isEnabled || !showIndicator) {
    return null;
  }

  const positionStyles = {
    "top-left": { top: "16px", left: "16px" },
    "top-right": { top: "16px", right: "16px" },
    "bottom-left": { bottom: "16px", left: "16px" },
    "bottom-right": { bottom: "16px", right: "16px" },
  };

  return (
    <div
      style={{
        position: "fixed",
        ...positionStyles[position],
        zIndex: 9999,
        backgroundColor: isConnected ? "#10b981" : "#ef4444",
        color: "white",
        padding: "8px 12px",
        borderRadius: "6px",
        fontSize: "12px",
        fontFamily: "monospace",
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        transition: "all 0.2s ease",
      }}
    >
      <div
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: "currentColor",
          opacity: isConnected ? 1 : 0.75,
        }}
      />
      <span>JSON Hot-Reload {isConnected ? "Connected" : "Disconnected"}</span>
      {lastUpdate && (
        <span style={{ opacity: 0.8, fontSize: "10px" }}>
          Updated: {lastUpdate.toLocaleTimeString()}
        </span>
      )}
    </div>
  );
}

/**
 * JSON Hot-Reload Provider Component
 *
 * Wraps the application to provide hot-reload functionality
 */
interface JSONHotReloadProviderProps {
  children: React.ReactNode;
  enabled?: boolean;
  showStatus?: boolean;
  statusPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
}

export function JSONHotReloadProvider({
  children,
  enabled = true,
  showStatus = true,
  statusPosition = "bottom-right",
}: JSONHotReloadProviderProps) {
  // Initialize hot-reload functionality
  useJSONHotReload(enabled);

  return (
    <>
      {children}
      {showStatus && (
        <JSONHotReloadStatus
          enabled={enabled}
          showIndicator={showStatus}
          position={statusPosition}
        />
      )}
    </>
  );
}
